import { pushErrorEvent, pushSelectMembershipEvent } from '../gtm/checkoutFlow';
import { updateMiniCart } from './orderSummary';
import { dynamicCountdown } from '../util';
import { equalizeTiles } from '../membershipTilesSwiper'; // eslint-disable-line import/no-cycle
import payementInitialize from './paymentScheduleUpdate';

/**
 * Equalizes inline height of all elements with the same selector
 * @param {string} selector - CSS class or id
 */
const equalizeElements = (selector) => {
    const elements = $(selector);
    if (elements.length === 0) return;

    // Reset any previously set min-height
    elements.css('min-height', '');

    let minHeight = 0;
    const modifiedParents = new Set();

    // Step 1: Temporarily force-show hidden parent elements
    elements.each((index, el) => {
        const $el = $(el);

        $el.parents().each((parentIndex, parent) => {
            const $parent = $(parent);

            const wasHidden = $parent.is(':hidden');
            const notAlreadyShown = !$parent.data('was-temp-shown');

            if (wasHidden && notAlreadyShown) {
                $parent
                    .data('was-temp-shown', true)
                    .data('original-style', $parent.attr('style') || '');

                // Apply display: block !important safely
                $parent[0].style.setProperty('display', 'block', 'important');

                modifiedParents.add($parent);
            }
        });
    });

    // Step 2: Measure heights
    elements.each((elementIndex, el) => {
        const height = $(el).outerHeight();
        if (height > minHeight) {
            minHeight = height;
        }
    });

    // Step 3: Restore hidden parents to original state
    modifiedParents.forEach(($parent) => {
        const original = $parent.data('original-style');
        if (original) {
            $parent.attr('style', original);
        } else {
            $parent.removeAttr('style');
        }
        $parent.removeData('was-temp-shown');
        $parent.removeData('original-style');
    });

    // Step 4: Apply the max height
    elements.css('min-height', `${minHeight}px`);
};

/**
 * Updates membership step
 * @param {Object} data - Payload data from ajax call
 */
const updateMembershipBlock = (data) => {
    const $membershipStep = $('.membership-step');
    $membershipStep.empty().append(data.membershipsData);
};

/**
 * Resets membership tables
 * @param {HTMLElements} $membershipTables - List of membership tables
 */
const resetMembershipTables = ($membershipTables) => {
    $membershipTables.each((i, table) => {
        const $membershipTable = $(table);
        $membershipTable.removeClass('highlighted');
        $membershipTable.removeClass('selected');
    });
};

/**
 * Resets membership duration wrapper for the toggle version
 * @param {HTMLElement} $duration - Membership duration block to be displayed
 */
const resetMembershipDurationWrapper = ($duration) => {
    const $durationWrapper = $duration.closest('.js-membership-duration-wrapper');

    // Extract the plan name prefix (e.g. "comfort-year" from "comfort-year-plan")
    const classList = $duration.attr('class').split(/\s+/);
    const planClass = classList.find((cls) => cls.endsWith('-plan'));
    const planPrefix = planClass ? planClass.replace(/-plan$/, '') : null;

    if (planPrefix) {
        // Remove any old plan-type classes first
        $durationWrapper
            .removeClass((index, className) => (className.match(/\b(?:comfort|premium|basic|ultimate)-(?:year|flex)\b/g) || []).join(' '))
            .addClass(planPrefix);
    }
};

/**
 * Handles click on the membership table button for the toggle version
 * @param {HTMLElement} $membershipButton - Clicked membership table button
 */
const handleMembershipButtonClick = ($membershipButton) => {
    const $membershipTables = $('.js-membership-block');
    const $membershipStepWrapper = $membershipButton.closest('.js-step-02-content');
    const $membershipTable = $membershipButton.closest('.js-membership-block');
    const $membershipTableInner = $membershipButton.closest('.js-membership-block-inner');
    const $nextStepButton = $membershipStepWrapper.find('.js-next-step');
    const membershipUrl = $membershipTableInner.attr('data-url');
    const membershipName = $membershipTableInner.attr('data-name');
    const membershipDuration = $membershipTableInner.attr('data-duration');
    const $duration = $membershipStepWrapper.find(`.${membershipName}-${membershipDuration}-plan`);
    const durationId = $duration.attr('data-pid');

    resetMembershipTables($membershipTables);
    resetMembershipDurationWrapper($duration);
    addMembershipPlanToCart(membershipUrl, durationId, true); // eslint-disable-line no-use-before-define

    $membershipTable.addClass('highlighted');
    $membershipTable.addClass('selected');
    $nextStepButton.removeClass('btn--disabled');
};

/**
 * Sends AJAX call to add membership plan to cart
 * @param {string} url - Url to send ajax call to
 * @param {string} pid - Membership duration plan id
 * @param {boolean} isMembershipButtonClick - True if the table membership button was clicked, False otherwise
 */
function addMembershipPlanToCart(url, pid, isMembershipButtonClick) {
    $('.js-checkout-order-summary').css('pointer-events', 'none');

    $.ajax({
        url,
        method: 'POST',
        data: {
            pid,
        },
        success: ({
            error,
            errorMessage,
            gtmProducts,
            gtmProductsNew,
        }) => {
            if (error) {
                pushErrorEvent('checkout', errorMessage || error.errorText);
                return;
            }

            const $checkoutStage = $('.js-data-checkout-stage');

            equalizeTiles();
            payementInitialize();
            updateMiniCart();

            if (gtmProducts) {
                $checkoutStage.data('gtm-products', gtmProducts);
            }

            if (gtmProductsNew) {
                $checkoutStage.data('gtm-membership-products', gtmProductsNew);

                if (isMembershipButtonClick) {
                    const products = typeof gtmProductsNew === 'string' ? JSON.parse(gtmProductsNew) : gtmProductsNew;
                    pushSelectMembershipEvent(products);
                }
            }
        },
        error: ({
            error,
            errorMessage,
        }) => {
            $('.js-checkout-order-summary').css('pointer-events', 'auto');
            pushErrorEvent('checkout', errorMessage || error.errorText);
        },
    });
}

/**
 * Binds click event to the membership selection buttons
 */
function handleMembershipSelection() {
    const $membershipButtons = $('.js-membership-btn-new');
    const $membershipToggleButtonsWrapper = $('.js-toggle-buttons');
    const $membershipToggleButtons = $('.js-membership-toggle');
    const $membershipToggleSubText = $('.js-toggle-sub-text');
    const $membershipYearToggleButton = $('.js-membership-toggle#year');
    const $membershipFlexToggleButton = $('.js-membership-toggle#flex');
    const $yearMembershipBlocks = $('.js-membership-block-inner.year');
    const $flexMembershipBlocks = $('.js-membership-block-inner.flex');

    $membershipButtons.each((i, button) => {
        const $membershipButton = $(button);
        $membershipButton.on('click', () => {
            handleMembershipButtonClick($membershipButton);
        });
    });

    $membershipToggleButtons.each((i, button) => {
        const $membershipToggleButton = $(button);
        $membershipToggleButton.on('click', () => {
            const id = $membershipToggleButton.attr('id');
            const selectedId = $('.js-membership-toggle.toggle-button--active').attr('id');

            if (id === selectedId) {
                return;
            }

            const isYear = id === 'year';
            const subText = isYear ? $membershipToggleSubText.attr('data-year-sub-text') : $membershipToggleSubText.attr('data-flex-sub-text');
            const $selectedMembership = $('.js-membership-block.selected');
            const isSelectedMembership = $selectedMembership.length > 0;

            $membershipToggleButton.addClass('toggle-button--active');
            $membershipToggleSubText.html(subText);

            if (isYear) {
                $yearMembershipBlocks.removeClass('display-none');
                $flexMembershipBlocks.addClass('display-none');
                $membershipFlexToggleButton.removeClass('toggle-button--active');
                $membershipToggleButtonsWrapper.removeClass('toggle-flex--active');
                $membershipToggleButtonsWrapper.addClass('toggle-year--active');
            } else if (id === 'flex') {
                $yearMembershipBlocks.addClass('display-none');
                $flexMembershipBlocks.removeClass('display-none');
                $membershipYearToggleButton.removeClass('toggle-button--active');
                $membershipToggleButtonsWrapper.removeClass('toggle-year--active');
                $membershipToggleButtonsWrapper.addClass('toggle-flex--active');
            }

            if (isSelectedMembership) {
                const $membershipButton = $selectedMembership.find(`.js-membership-block-inner.${id} .js-membership-btn-new`);
                handleMembershipButtonClick($membershipButton);
            }
        });
    });

    dynamicCountdown();
}

/**
 * Handles redirect from the club details page to the checkout step 2
 */
const handleRedirectFromCDP = () => {
    const $membershipTables = $('.js-membership-block');
    const membershipTablesArray = $membershipTables.get();
    const membershipTable = membershipTablesArray && membershipTablesArray.find((table) => $(table).hasClass('selected'));

    if (membershipTable) {
        const $membershipTable = $(membershipTable);
        const $membershipStepWrapper = $membershipTable.closest('.js-step-02-content');
        const $membershipDurationWrapper = $membershipStepWrapper.find('.js-membership-duration-wrapper');
        const selectedDuration = $('.js-membership-toggle.toggle-button--active').attr('id');
        const $membershipTableInner = $membershipTable.find(`.js-membership-block-inner.${selectedDuration}`);
        const membershipUrl = $membershipTableInner.attr('data-url');
        const membershipName = $membershipTableInner.attr('data-name');
        const membershipDuration = $membershipTableInner.attr('data-duration');
        const $selectedMembershipDuration = $membershipDurationWrapper.find(`.${membershipName}-${membershipDuration}-plan`);
        const durationId = $selectedMembershipDuration.attr('data-pid');

        addMembershipPlanToCart(membershipUrl, durationId, false);
    }
};

/**
 * Retrieves selected membership data
 * @returns {Object} - Membership information
 */
const getPreselectedMembershipInfo = () => {
    const $selectedMembershipTable = $('.js-membership-block.selected.highlighted');
    const membershipName = $selectedMembershipTable.attr('data-name');

    if (!membershipName) {
        return null;
    }

    const $selectedMembershipDuration = $(`.js-membership-contract-length[data-membership-name=${membershipName}][type=radio].checked`);
    const name = membershipName.charAt(0).toUpperCase() + membershipName.slice(1).toLowerCase();
    const duration = $selectedMembershipDuration.attr('data-duration');

    return {
        name,
        duration,
    };
};

/**
 * Handles counter in the checkout banner on step 2
 */
const countdown = () => {
    const $counter = $('#top-counter');

    if ($counter.length > 0) {
        const $counterWrapper = $('#top-counter-wrapper');
        const $daysValue = $('.js-top-counter-days');
        const $hoursValue = $('.js-top-counter-hours');
        const $minutesValue = $('.js-top-counter-minutes');
        const date = $counter.attr('data-date');
        const countDownDate = new Date(date).getTime();

        const interval = setInterval(() => {
            const now = new Date().getTime();
            const distance = countDownDate - now;

            const days = Math.floor(distance / (1000 * 60 * 60 * 24));
            const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));

            $daysValue.text(days);
            $hoursValue.text(hours);
            $minutesValue.text(minutes);

            if (distance < 0) {
                clearInterval(interval);
                $counterWrapper.addClass('d-none');
            }
        }, 1000);
    }
};

export {
    updateMembershipBlock,
    handleMembershipSelection,
    handleRedirectFromCDP,
    getPreselectedMembershipInfo,
    countdown,
    equalizeElements,
};
